<template>
    <div class="container-fluid">
        <div class="row">
            <!-- Sidebar for Form Controls -->
            <div class="col-md-3 sidebar bg-light">
                <div class="info-panel p-3">
                    <h2 class="mb-4">{{ title }}</h2>

                    <!-- Species Selection -->
                    <div class="form-group">
                        <label for="species-select">Select Plant Species</label>
                        <select id="species-select" v-model="selectedSpecies" class="form-control">
                            <option value="">All</option>
                            <option v-for="species in availableSpecies" :key="species" :value="species">
                                {{ species }}
                            </option>
                        </select>
                    </div>

                    <!-- Parameter Selection -->
                    <div class="form-group mt-3">
                        <label for="parameter-select">Select Hydrological Indicator</label>
                        <select id="parameter-select" v-model="selectedParameter" class="form-control">
                            <option v-for="(param, index) in parameterNames" :key="index" :value="param">{{
                                param }}
                            </option>
                        </select>
                    </div>

                    <!-- Date Selection -->
                    <!--
                    <div class="form-group mt-3">
                        <label for="date-select">Select Date</label>
                        <Datepicker v-model="selectedDate" range auto-apply :disabled-dates="disabledDateHandler" />
                    </div>
                    -->

                    <div class="form-group mt-3">
                        <label for="date-select">Select Start Year</label>
                        <Datepicker v-model="selectedStartDate" year-picker auto-apply :year-range="dynamicYearRange" />
                    </div>

                    <div class="form-group mt-3">
                        <label for="date-select">Select End Year</label>
                        <Datepicker v-model="selectedEndDate" year-picker auto-apply :year-range="dynamicYearRange" />
                    </div>

                    <div class="form-group mt-3">
                        <label for="temporal-select">Select Temporal Aggregation Method
                            (Optional)</label>
                        <select id="temporal-select" v-model="selectedTemporalAggregation" class="form-control">
                            <option>DAILY</option>
                            <option>WEEKLY</option>
                            <option>MONTHLY</option>
                            <option>ANNUAL</option>
                        </select>
                    </div>

                    <!-- Display All Matching IDs -->
                    <div v-if="selectedSpecies !== null" class="mt-3">
                        <p>Matching Models:</p>
                        <ul>
                            <li v-for="id in selectedIDs" :key="id">{{ id }}</li>
                        </ul>
                    </div>

                    <!-- Button to Open Map for Spatial Extent Selection -->
                    <div class="mt-4">
                        <button class="btn btn-secondary" @click="showMapModal = true">
                            Select Spatial Extent
                        </button>
                    </div>

                    <!-- Fetch Forestry Data Button -->
                    <div class="mt-3">
                        <button class="btn btn-primary" :disabled="!selectedIDs.length || isLoading || !selectedStartDate || !selectedEndDate"
                            @click="fetchForestryData">
                            {{ isLoading ? 'Fetching...' : 'Fetch Forestry Data' }}
                        </button>
                    </div>

                </div>
            </div>

            <!-- Main Content: Forestry Chart -->

            <div class="col-md-9 d-flex flex-column gap-3">
                <h4>Forestry Data Plot</h4>
                <!-- Loading Indicator -->
                <div v-if="isLoading" class="loading-indicator mt-2">
                    <div class="spinner"></div>
                    <p>Fetching forestry data, please wait...</p>
                </div>

                <!-- Keep the plot container always in DOM so template ref is stable -->
                <div id="plotlyChartContainer" class="mt-4">
                    <h5 v-if="apiResponse && apiResponse.length">Forestry Projections: {{ selectedTemporalAggregation }} Means</h5>
                    <div ref="plotlyChart" style="width: 100%; height: 500px;"></div>
                </div>

                <div v-if="apiResponse && apiResponse.length">
                    <div id="downloadRawData"></div>

                    <div v-if="Object.keys(statistics).length > 0" class="mt-4">
                        <h5>Statistics</h5>
                        <ul class="list-group">
                            <li v-for="(value, key) in statistics" :key="key" class="list-group-item">
                                <strong>{{ key }}:</strong> {{ value }}
                            </li>
                        </ul>
                    </div>

                    <div id="spatialExtentMain" class="mb-4">
                        <h4>Spatial Extent</h4>
                        <MapboxMap v-if="initialBBOX.length > 0" id="mapMain" :selectedParameter="selectedParameter" :selectedIDs="selectedIDs"
                            :initialBBOX="initialBBOX" :currentBbox="currentBbox" :redrawBbox="false"
                            @update-bbox="updateBbox" />
                    </div>

                    <div>
                        <button class="btn btn-secondary" :disabled="!selectedIDs.length || isLoading || isDownloading"
                            @click="downloadRawData(selectedIDs)">
                            <BIconDownload class="icon-margin" />
                            {{ isDownloading ? 'Preparing Download...' : 'Download Raw Data' }}
                        </button>
                    </div>
                </div>

                <div v-else>
                    <!-- Placeholder to display when no chart has been generated yet -->
                    <div class="placeholder-container">
                        <p class="placeholder-text">No data available yet. Please select parameters and fetch data.</p>
                    </div>
                </div>

            </div>
        </div>

        <!-- Modal for Mapbox Map -->
        <div v-if="showMapModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Select Spatial Extent</h5>
                    <button type="button" class="close" @click="closeMapModal">&times;</button>
                </div>
                <div class="modal-body">
                    <MapboxMap v-if="initialBBOX.length > 0" :selectedParameter="selectedParameter" :selectedIDs="selectedIDs"
                        :initialBBOX="initialBBOX" :currentBbox="currentBbox" @update-bbox="updateBbox" />
                </div>
                <div class="modal-footer">
                    <button class="btn btn-primary" @click="closeMapModal">Done</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed, nextTick, watch } from 'vue';
import MapboxMap from './MapComponent.vue';
import Datepicker from '@vuepic/vue-datepicker';
import Plotly from 'plotly.js-dist';
import '@vuepic/vue-datepicker/dist/main.css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { BIconDownload } from 'bootstrap-icons-vue';

export default {
    name: 'ForestryExplorerTool',
    components: {
        MapboxMap,
        Datepicker,
        BIconDownload,
    },
    setup() {
        const title = ref('Tool parameters');
        const schemaKeys = ref([]);
        const parameterNames = ref([]);
        const initialBBOX = ref([]);
        const spatialRes = ref([]);
        const parsedIds = ref([]);
        const selectedDate = ref();
        const selectedStartDate = ref();
        const selectedEndDate = ref();
        const selectedTemporalAggregation = ref();
        const dateRange = ref([null, null]);
        const selectedParameter = ref('temperature_mean');
        const selectedSpecies = ref(null);
        const apiResponse = ref(null);
        const isLoading = ref(false);
        const isDownloading = ref(false);
        const showMapModal = ref(false);
        const statistics = ref({});
        const plotlyChart = ref(null);

        const fetchInitialBbox = async (collectionId) => {
            console.log('Fetching initial BBOX for collectionId:', collectionId);
            try {
                // Use cached collection detail from local server (prefetched by server.js)
                const response = await fetch(`/api/collections/${encodeURIComponent(collectionId)}`);
                const schema = await response.json();
                console.log('Received schema for BBOX:', schema);
                if (schema.extent?.spatial?.bbox?.[0]) {
                    initialBBOX.value = schema.extent.spatial.bbox[0];
                    console.log('Setting initialBBOX.value to:', initialBBOX.value);
                    spatialRes.value = [schema.extent.spatial.grid[0].resolution, schema.extent.spatial.grid[1].resolution]
                } else {
                    console.error('BBOX not found in response for collection:', collectionId);
                }
            } catch (error) {
                console.error('Error fetching initial BBOX:', error);
            }
        };

        const fetchSchemaKeys = async (collectionId) => {
            if (!collectionId) return;
            try {
                // Use cached collection detail from local server (prefetched by server.js)
                const response = await fetch(`/api/collections/${encodeURIComponent(collectionId)}`);
                const schema = await response.json();

                if (schema.parameter_names) {
                    parameterNames.value = Object.keys(schema.parameter_names);
                    schemaKeys.value = Object.entries(schema.parameter_names);
                } else {
                    console.error('parameter_names not found in schema for collection:', collectionId);
                }
            } catch (error) {
                console.error('Error fetching schema keys:', error);
            }
        };

        const fetchAndProcessData = async () => {
            try {
                // Request collection list from local cache server (prefetched by server.js)
                const response = await fetch('/api/collections');
                const data = await response.json();
                if (data.collections && data.collections.length > 0) {
                    parsedIds.value = data.collections
                        .map(c => parseId(c.id))
                        .filter(id => id !== null);

                    const referenceCollection = data.collections.find(c => c.id.startsWith('Forestry_Data_Projection'));

                    if (referenceCollection) {
                        await fetchInitialBbox(referenceCollection.id);
                        await fetchSchemaKeys(referenceCollection.id);
                    } else {
                        console.error('No collection starting with "Forestry_Data_Projection" found to determine initial BBOX and schema.');
                    }
                }
            } catch (error) {
                console.error('Error fetching or processing data:', error);
            }
        };

        const parseId = (id) => {
            // Only include IDs starting with "Forestry_Data_Projection_"
            if (!id.startsWith('Forestry_Data_Projection_')) {
                return null;
            }

            const parts = id.split('_');
            
            if (parts.length >= 4) {
                const species = parts[parts.length - 1];
                return { id, species };
            }

            return null;
        };

        const availableSpecies = computed(() => {
            return [...new Set(parsedIds.value.map(id => id.species))];
        });

        const selectedIDs = computed(() => {
            return parsedIds.value
                .filter(id => !selectedSpecies.value || id.species === selectedSpecies.value)
                .map(id => id.id);
        });

        const downloadRawData = async (selectedIDs) => {
            isDownloading.value = true; // Set loading state for download
            console.log("[DEBUG] Downloading raw data for IDs:", selectedIDs);

            const zip = new JSZip();
            const baseUrl = 'https://klimakonform-maps.geo.tu-dresden.de/pygeoapi/collections/';

            // Fetch data as netCDF for each ID asynchronously and name it like the ID => {id}.nc if multiple IDs are selected then create a zip file including all netCDF files
            const fetchPromises = selectedIDs.map(async id => {
                const res = await fetch(`${baseUrl}${id}/coverage?f=NetCDF`);
                const blob = await res.blob();
                zip.file(`${id}.nc`, blob);
            });
            try {
                await Promise.all(fetchPromises);
                const content = await zip.generateAsync({ type: 'blob' });
                saveAs(content, 'forestry_data.zip');
            } catch (error) {
                console.error('Error downloading raw data:', error);
            }
            isDownloading.value = false; // Reset loading state after download
            console.log("[DEBUG] Download completed.");
        }
        async function vailidateTimeCoverage(ids) {
            // Use cached collection detail from local server (prefetched by server.js)
            const fetchPromises = ids.map(id => fetch(`/api/collections/${encodeURIComponent(id)}`).then(res => res.json()));

            try {
                const results = await Promise.all(fetchPromises);

                // Extract the temporal intervals for each collection
                const intervals = results.map(result => result.extent.temporal.interval[0]);

                // Initialize intersection range with the first collection's date range
                let intersectionStart = new Date(intervals[0][0]);
                let intersectionEnd = new Date(intervals[0][1]);

                intervals.forEach(interval => {
                    const collectionStart = new Date(interval[0]);
                    const collectionEnd = new Date(interval[1]);

                    // The start of the intersection is the latest start date
                    if (collectionStart > intersectionStart) {
                        intersectionStart = collectionStart;
                    }

                    // The end of the intersection is the earliest end date
                    if (collectionEnd < intersectionEnd) {
                        intersectionEnd = collectionEnd;
                    }
                });
                // If the intersection is valid (start date is before end date), return it
                if (intersectionStart < intersectionEnd) {
                    console.log("[DEBUG] Possible date range: " + [intersectionStart.getFullYear(), intersectionEnd.getFullYear()]);

                    return {
                        minDate: intersectionStart.toISOString(),
                        maxDate: intersectionEnd.toISOString()
                    };
                } else {
                    // No valid intersection (e.g., no overlap), return null or appropriate value
                    console.warn('No valid intersection found for the selected collections.');
                    return null;
                }
            } catch (error) {
                console.error('Error fetching or processing data:', error);
            }
        }

        const calculateDynamicYearRange = () => {
            const startYear = dateRange.value[0].getFullYear();
            const endYear = dateRange.value[1].getFullYear();
            console.log("Possible time range: " + [startYear, endYear])
            return [startYear, endYear];
        }
        // Dynamically calculate the year range
        const dynamicYearRange = computed(() => {
            if (dateRange.value[0] && dateRange.value[1]) {
                return calculateDynamicYearRange();
            }
            return [2020, 2040]; // Default year range if dateRange is not defined
        });


        const disabledDateHandler = (date) => {
            return date < dateRange.value[0] || date > dateRange.value[1];
        };

        // Function to fetch min/max date range when selectedIds changes
        const fetchDateRange = async (ids) => {
            if (!ids || ids.length === 0) return;

            try {
                const period = await vailidateTimeCoverage(ids);
                dateRange.value = [
                    new Date(period.minDate),
                    new Date(period.maxDate)
                ];
            } catch (error) {
                console.error('Error fetching date range:', error);
            }
        };
        watch(selectedStartDate, (newVal) => {
            if (newVal && !(newVal instanceof Date)) {
                selectedStartDate.value = newVal;
                dynamicYearRange.value[0] = newVal;
            }
            if (newVal == null) {
                // Fetch the original date range again when start date is reset
                fetchDateRange(selectedIDs).then(() => {
                    // After fetching, dynamicYearRange should be updated automatically
                    dynamicYearRange.value[0] = calculateDynamicYearRange()[0];

                });
            }
        });

        watch(selectedEndDate, (newVal) => {
            if (newVal && !(newVal instanceof Date)) {
                selectedEndDate.value = newVal;
            }
        });

        // Watch for changes in selectedIds and call fetchDateRange
        watch(selectedIDs, (newIds) => {
            fetchDateRange(newIds);
        }, { immediate: true }); // { immediate: true } triggers the watcher on component mount as well



        const currentBbox = ref(null);

        const updateBbox = (bbox) => {
            const [minX, minY, maxX, maxY] = bbox;
            const [resX, resY] = spatialRes.value;

            let adjusted = false;

            if (maxX - minX < resX) {
                bbox[2] = minX + resX;
                adjusted = true;
            }

            if (maxY - minY < resY) {
                bbox[3] = minY + resY;
                adjusted = true;
            }

            if (adjusted) {
                console.log("Bounding box adjusted to meet minimum spatial resolution");
                console.log('Updated bbox:', bbox);

            }
            currentBbox.value = bbox;
        };


        const closeMapModal = () => {
            showMapModal.value = false; // Close modal on user action
        };
        const fetchForestryData = async () => {
            isLoading.value = true;
            console.log("[REQUEST]: selectedTemporalAggregation: ", selectedTemporalAggregation.value)
            const getYear = (date) => {
                if (date instanceof Date) {
                    return date.getFullYear();
                }
                return date;
            };
            const postData = {
                collection_ids: selectedIDs.value,
                climate_indicator: selectedParameter.value,
                start_year: getYear(selectedStartDate.value).toString(),
                end_year: getYear(selectedEndDate.value).toString(),
                bbox: currentBbox.value || initialBBOX.value,
                temporal_resolution: selectedTemporalAggregation.value
            };
            try {
                const response = await fetch('/api/explorer-data', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(postData)
                });
                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result.message || 'Failed to fetch data.');
                }
                const plotData = processForestryData(result);
                apiResponse.value = plotData.plotData;
                statistics.value = calculateStatistics(plotData.plotData, selectedParameter.value, selectedTemporalAggregation.value);

                isLoading.value = false;

                // Wait for DOM to update after setting apiResponse and isLoading
                await nextTick();

                // Only attempt plotting when there is actual plot data
                if (plotData.plotData && plotData.plotData.length > 0) {
                    plotForestryData(plotData);
                } else {
                    console.warn('No plot data available after processing; skipping plot.');
                }

                return result;
            } catch (error) {
                console.error('Error fetching forestry data:', error);
                isLoading.value = false;
            }
        };

        const processForestryData = (data) => {
            const plotData = [];
            const allValues = [];

            for (const model in data.value) {
                const modelData = data.value[model];

                // Prefer metadata.datetime (ISO range), fall back to domain.axes.time.values if present
                if (modelData.metadata && modelData.metadata.datetime) {
                    const [startStr, endStr] = modelData.metadata.datetime.split('/');
                    const startDate = new Date(startStr);
                    const endDate = new Date(endStr);
                    const values = Array.isArray(modelData.values) ? modelData.values : [];
                    const numPoints = values.length;

                    if (numPoints === 0) {
                        console.warn('No values for model:', model);
                        continue;
                    }

                    const timeDiff = endDate - startDate;
                    const avgStep = timeDiff / Math.max(1, (numPoints - 1));

                    let dates = [];
                    let currentDate = new Date(startDate);

                    for (let i = 0; i < numPoints; i++) {
                        dates.push(currentDate.toISOString().split('T')[0]);

                        if (avgStep >= 365 * 24 * 60 * 60 * 1000) {
                            currentDate.setFullYear(currentDate.getFullYear() + 1);
                        } else if (avgStep >= 30 * 24 * 60 * 60 * 1000) {
                            currentDate.setMonth(currentDate.getMonth() + 1);
                        } else if (avgStep >= 7 * 24 * 60 * 60 * 1000) {
                            currentDate.setDate(currentDate.getDate() + 7);
                        } else {
                            currentDate.setDate(currentDate.getDate() + 1);
                        }
                    }

                    plotData.push({
                        x: dates,
                        y: values,
                        name: model,
                        type: 'scatter',
                        mode: 'lines',
                        line: { shape: 'linear' }
                    });
                    allValues.push(...values);
                } else if (modelData.metadata?.domain?.axes?.time?.values && Array.isArray(modelData.metadata.domain.axes.time.values)) {
                    // domain.axes.time.values contains explicit timestamps
                    const dates = modelData.metadata.domain.axes.time.values.map(v => {
                        // some backends provide "YYYY-MM-DD" or full ISO already
                        return (typeof v === 'string') ? v.split('T')[0] : new Date(v).toISOString().split('T')[0];
                    });
                    const values = Array.isArray(modelData.values) ? modelData.values : [];

                    if (dates.length !== values.length) {
                        console.warn(`Date/value length mismatch for model ${model}: dates=${dates.length} values=${values.length}`);
                    }

                    plotData.push({
                        x: dates,
                        y: values,
                        name: model,
                        type: 'scatter',
                        mode: 'lines',
                        line: { shape: 'linear' }
                    });
                    allValues.push(...values);
                } else {
                    console.error('Metadata or datetime are missing for model:', model);
                }
            }
            return { plotData, allValues };
        };

        const calculateStatistics = (plotData, parameter, temporalAggregation) => {
            const stats = {};
            const allValues = plotData.flatMap(trace => trace.y);

            if (allValues.length === 0) {
                return {};
            }

            const mean = allValues.reduce((sum, val) => sum + val, 0) / allValues.length;
            const min = Math.min(...allValues);
            const max = Math.max(...allValues);

            const keys = schemaKeys.value || [];
            const index = keys.findIndex(key => key[0] === parameter);
            const unitSymbol = keys[index]?.[1]?.unit?.symbol?.value ?? '';

            stats['Mean'] = `${mean.toFixed(2)} ${unitSymbol}`;
            stats['Min'] = `${min.toFixed(2)} ${unitSymbol}`;
            stats['Max'] = `${max.toFixed(2)} ${unitSymbol}`;

            if (temporalAggregation === 'DAILY') {
                if (parameter.includes('temperature')) {
                    const heatDaysThreshold = 30; // Celsius
                    const frostDaysThreshold = 0; // Celsius

                    let heatDays = 0;
                    let frostDays = 0;
                    let heatingDegreeDays = 0;
                    let coolingDegreeDays = 0;

                    plotData.forEach(trace => {
                        trace.y.forEach(value => {
                            if (value > heatDaysThreshold) {
                                heatDays++;
                            }
                            if (value < frostDaysThreshold) {
                                frostDays++;
                            }
                            if (value < 18) {
                                heatingDegreeDays += (18 - value);
                            }
                            if (value > 18) {
                                coolingDegreeDays += (value - 18);
                            }
                        });
                    });
                    stats['Heat Days (>30°C)'] = heatDays;
                    stats['Frost Days (<0°C)'] = frostDays;
                    stats['Heating Degree Days (Base 18°C)'] = heatingDegreeDays.toFixed(2);
                    stats['Cooling Degree Days (Base 18°C)'] = coolingDegreeDays.toFixed(2);
                } else if (parameter.includes('precipitation')) {
                    const wetDayThreshold = 1; // mm

                    let totalPrecipitation = 0;
                    let wetDays = 0;
                    let heavyPrecipitationDays = 0;

                    plotData.forEach(trace => {
                        trace.y.forEach(value => {
                            totalPrecipitation += value;
                            if (value > wetDayThreshold) {
                                wetDays++;
                            }
                            if (value >= 10) { // R10mm: days with precipitation >= 10mm
                                heavyPrecipitationDays++;
                            }
                        });
                    });
                    stats['Total Precipitation'] = `${totalPrecipitation.toFixed(2)} mm`;
                stats['Wet Days (>1mm)'] = wetDays;
                stats['Heavy Precipitation Days (>=10mm)'] = heavyPrecipitationDays;
            } else if (parameter.includes('wind')) {
                // No specific daily metrics beyond mean, min, max with current data structure
                // Future enhancement: consider wind gusts if data becomes available
            } else if (parameter.includes('humidity')) {
                // No specific daily metrics beyond mean, min, max with current data structure
            } else if (parameter.includes('radiation')) {
                let totalRadiation = 0;
                plotData.forEach(trace => {
                    trace.y.forEach(value => {
                        totalRadiation += value;
                    });
                });
                stats['Total Radiation'] = `${totalRadiation.toFixed(2)} W/m²`;
            }
            }
            return stats;
        };


        const getDynamicTimeAxis = (dates) => {
            const msPerDay = 24 * 60 * 60 * 1000;
            const msPerYear = 365.25 * msPerDay;
            const timeRange = new Date(dates[dates.length - 1]) - new Date(dates[0]);

            let tickformat, dtick;

            if (timeRange <= 31 * msPerDay) {
                tickformat = '%Y-%m-%d';
                dtick = 'D1';
            } else if (timeRange <= 2 * msPerYear) {
                tickformat = '%Y-%m';
                dtick = 'M1';
            } else if (timeRange <= 20 * msPerYear) {
                tickformat = '%Y';
                dtick = 'M12';
            } else {
                tickformat = '%Y';
                dtick = 'M60';
            }

            return { tickformat, dtick };
        };


        const plotForestryData = (dataObject) => {
            if (!dataObject || !dataObject.plotData || dataObject.plotData.length === 0) {
                return;
            }

            const plotData = dataObject.plotData;
            const keys = schemaKeys.value || [];
            const index = keys.findIndex(key => key[0] === selectedParameter.value);
            const nextKey = index !== -1 ? keys[index] : null;

            const unitName = nextKey?.[1]?.name ?? '';
            const unitSymbol = nextKey?.[1]?.unit?.symbol?.value ?? '';
            const unit = unitName ? `${unitName} in [${unitSymbol}]` : '';

            const allDates = plotData.flatMap(trace => trace.x);
            const { tickformat: initialTickFormat, dtick: initialDtick } = getDynamicTimeAxis(allDates);

            const layout = {
                xaxis: {
                    title: 'Date',
                    type: 'date',
                    tickformat: initialTickFormat,
                    dtick: initialDtick,
                    rangeslider: { visible: true },
                    rangeselector: {
                        buttons: [
                            { count: 1, label: '1m', step: 'month', stepmode: 'backward' },
                            { count: 6, label: '6m', step: 'month', stepmode: 'backward' },
                            { count: 1, label: '1y', step: 'year', stepmode: 'backward' },
                            { step: 'all' }
                        ]
                    },
                    range: [allDates[0], allDates[allDates.length - 1]]
                },
                yaxis: { title: unit },
                legend: { orientation: 'h' }
            };

            // Resolve container (prefer template ref, fallback to DOM)
            const container = plotlyChart.value || document.querySelector('#plotlyChartContainer div') || document.getElementById('plotlyChartContainer');

            if (!container) {
                console.error('Plot container not found, aborting plot.');
                return;
            }

            Plotly.newPlot(container, plotData, layout).then(() => {
                const msPerDay = 24 * 60 * 60 * 1000;
                const msPerYear = 365.25 * msPerDay;

                // Attach relayout handler only if element exposes "on"
                if (container && typeof container.on === 'function') {
                    container.on('plotly_relayout', (eventData) => {
                        if (eventData['xaxis.autorange']) {
                            Plotly.relayout(container, {
                                'xaxis.dtick': initialDtick,
                                'xaxis.tickformat': initialTickFormat
                            });
                            return;
                        }

                        if (eventData['xaxis.range[0]'] && eventData['xaxis.range[1]']) {
                            const start = new Date(eventData['xaxis.range[0]']);
                            const end = new Date(eventData['xaxis.range[1]']);
                            const rangeDuration = end - start;

                            if (rangeDuration <= msPerYear) {
                                Plotly.relayout(container, {
                                    'xaxis.dtick': 'M1',
                                    'xaxis.tickformat': '%b %Y'
                                });
                            } else {
                                Plotly.relayout(container, {
                                    'xaxis.dtick': initialDtick,
                                    'xaxis.tickformat': initialTickFormat
                                });
                            }
                        }
                    });
                }
            }).catch(err => {
                console.error('Plotly rendering error:', err);
            });
        };


        onMounted(() => {
            fetchAndProcessData();
        });

        return {
            title,
            selectedDate,
            selectedStartDate,
            selectedTemporalAggregation,
            selectedEndDate,
            selectedParameter,
            parameterNames,
            initialBBOX,
            currentBbox,
            selectedSpecies,
            selectedIDs,
            apiResponse,
            fetchForestryData,
            availableSpecies,
            isLoading,
            isDownloading,
            showMapModal,
            updateBbox,
            closeMapModal,
            vailidateTimeCoverage,
            downloadRawData,
            dateRange,
            disabledDateHandler,
            dynamicYearRange,
            statistics,
            plotlyChart
        };
    }
};
</script>


<style scoped>
.spatialExtentMain {
    padding-left: 10%;
}

.mapMain {
    width: 50%;
    padding-left: 10%
}

.col-md-3.sidebar {
    border-radius: 25px;
}

.row {
    padding-left: 5%;
    padding-right: 5%
}

.info-panel {
    border-radius: 25px;
}

.loading-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.modal-content {
    background-color: white;
    padding: 20px;
    width: 80%;
    max-width: 800px;
    border-radius: 8px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    margin-top: 10px;
}

.modal-footer {
    margin-top: 20px;
    text-align: right;
}

.intro-text {
    padding: 20px;
    text-align: center;
    margin-left: 20%;
    margin-right: 20%;
}

.icon-margin {
    margin-left: 10px;
}
</style>
